// Ringing modal for inbound Messenger Calling API calls. Stacked
// queue view (like the Twilio toast) — multiple customers calling at
// once each get their own card.
//
// The Accept button shows:
//   - "Answer"        when the customer is already in inboxes
//   - "Claim & Answer" when they're in pendingchats or cold-call (we
//                      promote them to an inboxes row server-side
//                      before signaling Meta to accept)
//
// During an active Messenger call, Accept is disabled with a tooltip
// (you can't take a second WebRTC call without hold/transfer infra).

import React from "react";
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MessageIcon from "@mui/icons-material/Message";
import { useMessengerCall } from "src/context/messengerCall/MessengerCallContext";
import useCallTone from "src/hooks/useCallTone";

const IncomingMessengerCallToast: React.FC = () => {
  const { hasIncoming, incomingQueue, isOnCall, acceptCall, rejectCall } =
    useMessengerCall();

  // Silence ringtone while on an active Messenger call.
  useCallTone(hasIncoming && !isOnCall ? "incoming" : "idle");

  if (!hasIncoming) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        // Slide below active call widget when on a call so the cards
        // never get hidden behind it.
        top: isOnCall ? 410 : 80,
        // Different right offset than the Twilio toast (right: 20) and
        // active widget (right: 40) so multiple stacks don't collide.
        right: 340,
        zIndex: 9998,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        maxHeight: isOnCall ? "calc(100vh - 440px)" : "calc(100vh - 120px)",
        overflowY: "auto",
        pr: "8px",
      }}
    >
      {incomingQueue.map((entry) => {
        const isClaimed = !!entry.inboxId;
        const primary = entry.customerName || "Messenger user";
        const acceptDisabled = isOnCall || !isClaimed;
        const acceptTooltip = (() => {
          if (isOnCall) return "Hang up the current call first";
          if (!isClaimed) return "Claim this chat in Pending Chats to enable";
          return "Answer call";
        })();

        return (
          <Paper
            key={entry.callId}
            elevation={6}
            sx={{
              p: 2,
              width: 300,
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.97)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 32px rgba(24, 119, 242, 0.18)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transition: "transform 0.2s ease",
              position: "relative",
              "&:hover": { transform: "translateX(-4px)" },
            }}
          >
            <Chip
              label="Messenger"
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                height: 18,
                fontSize: 10,
                color: "white",
                background: "#1877F2",
              }}
            />
            <Avatar
              src={entry.customerPicture || undefined}
              sx={{
                width: 64,
                height: 64,
                mb: 1,
                border: "2px solid #1877F2",
                bgcolor: "rgba(24, 119, 242, 0.08)",
              }}
            >
              <MessageIcon sx={{ color: "#1877F2", fontSize: 28 }} />
            </Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {primary}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mb: 0.5 }}
            >
              Incoming Messenger call
            </Typography>
            {!isClaimed && (
              <Typography
                variant="caption"
                sx={{
                  mb: 1,
                  color: "#F22F46",
                  fontWeight: 500,
                  textAlign: "center",
                  px: 1,
                }}
              >
                New customer — claim in Pending Chats first to answer
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <Tooltip title={acceptTooltip}>
                <span>
                  <IconButton
                    onClick={() => acceptCall(entry.callId)}
                    disabled={acceptDisabled}
                    sx={{
                      bgcolor: acceptDisabled ? "#cccccc" : "#1877F2",
                      color: "white",
                      "&:hover": {
                        bgcolor: acceptDisabled ? "#cccccc" : "#125ab8",
                      },
                      width: 48,
                      height: 48,
                    }}
                  >
                    <CallIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject call">
                <IconButton
                  onClick={() => rejectCall(entry.callId)}
                  sx={{
                    bgcolor: "#FF3B30",
                    color: "white",
                    "&:hover": { bgcolor: "#D70015" },
                    width: 48,
                    height: 48,
                  }}
                >
                  <CallEndIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default IncomingMessengerCallToast;
